import type { ReactNode } from 'react';
import { CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { ApprovalState } from '../clientApprovalPage.types';
import {
    ALREADY_APPROVED_MESSAGE,
    ALREADY_APPROVED_TITLE,
    LOAD_ERROR_MESSAGE,
    LOAD_ERROR_TITLE,
    LOADING_LABEL,
    RESULT_COPY,
} from '../clientApprovalPage.definitions';

interface ClientApprovalResultProps {
    state: ApprovalState;
    approvedAt?: string | null;
}

const ICON_CLASS = 'h-16 w-16 mx-auto';

export function ClientApprovalResult({ state, approvedAt }: ClientApprovalResultProps) {
    if (state === 'loading') {
        return (
            <div className="flex flex-col items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                <p className="text-gray-600">{LOADING_LABEL}</p>
            </div>
        );
    }

    if (state === 'already_approved') {
        return (
            <div className="text-center space-y-4">
                <CheckCircleIcon className={`${ICON_CLASS} text-green-600`} />
                <h2 className="text-2xl font-semibold text-gray-900">{ALREADY_APPROVED_TITLE}</h2>
                <p className="text-gray-600">
                    {ALREADY_APPROVED_MESSAGE}
                    {approvedAt ? ` em ${approvedAt}` : ''}.
                </p>
            </div>
        );
    }

    if (state === 'success') {
        return <Message icon={<CheckCircleIcon className={`${ICON_CLASS} text-green-600`} />} copy={RESULT_COPY.success} />;
    }

    if (state === 'correction_sent' || state === 'already_rejected') {
        return <Message icon={<ClockIcon className={`${ICON_CLASS} text-blue-600`} />} copy={RESULT_COPY[state]} />;
    }

    if (state === 'invalid_segments') {
        return <Message icon={<ClockIcon className={`${ICON_CLASS} text-blue-600`} />} copy={RESULT_COPY.invalid_segments} />;
    }

    if (state === 'expired') {
        return <Message icon={<ClockIcon className={`${ICON_CLASS} text-yellow-600`} />} copy={RESULT_COPY.expired} />;
    }

    if (state === 'closed') {
        return <Message icon={<XCircleIcon className={`${ICON_CLASS} text-red-600`} />} copy={RESULT_COPY.closed} />;
    }

    if (state === 'not_found') {
        return <Message icon={<XCircleIcon className={`${ICON_CLASS} text-red-600`} />} copy={RESULT_COPY.not_found} />;
    }

    return (
        <Message
            icon={<XCircleIcon className={`${ICON_CLASS} text-red-600`} />}
            copy={{ title: LOAD_ERROR_TITLE, message: LOAD_ERROR_MESSAGE }}
        />
    );
}

interface MessageProps {
    icon: ReactNode;
    copy: { title: string; message: string };
}

function Message({ icon, copy }: MessageProps) {
    return (
        <div className="text-center space-y-4">
            {icon}
            <h2 className="text-2xl font-semibold text-gray-900">{copy.title}</h2>
            <p className="text-gray-600">{copy.message}</p>
        </div>
    );
}
